import { useEffect } from 'react';
import { Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import TrashIcon from '@/assets/icons/trash-01.svg?react';
import { Button } from '@/components/button/button';
import { FormModal } from '@/components/form-modal/form-modal';
import IconButton from '@/components/icon-button/icon-button';
import Toggle from '@/components/toggle/toggle';
import {
  useCreateOutboundScenario,
  useUpdateOutboundScenario,
} from '@/hooks/use-outbound-scenarios/use-outbound-scenarios';
import { MAX_DATA_TO_COLLECT_KEYS } from '@/services/outbound-calls-service/outbound-calls.constants';
import {
  OutboundScenario,
  OutboundScenarioPayload,
} from '@/services/outbound-calls-service/scenarios.service.types';
import { openNotification } from '@/utils/notification/notification';
import { formFieldErrors } from '../outbound-calls.utils';
import styles from '../outbound-calls.module.scss';

type DataField = { key: string; description: string };

type ScenarioFormValues = {
  name: string;
  objective: string;
  success_criteria: string;
  opening_line?: string;
  guardrails: string[];
  data_to_collect: DataField[];
  active: boolean;
};

const FIELDS = [
  'name',
  'objective',
  'success_criteria',
  'opening_line',
  'guardrails',
  'data_to_collect',
  'active',
] as const satisfies readonly (keyof ScenarioFormValues)[];

// The key becomes a field of the call's extracted data, so it stays machine-friendly.
const DATA_KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_]*$/;

const toFormValues = (scenario: OutboundScenario): ScenarioFormValues => ({
  name: scenario.name,
  objective: scenario.objective,
  success_criteria: scenario.success_criteria,
  opening_line: scenario.opening_line ?? '',
  guardrails: scenario.guardrails ?? [],
  data_to_collect: Object.entries(scenario.data_to_collect ?? {}).map(([key, value]) => ({
    key,
    description: typeof value === 'string' ? value : JSON.stringify(value),
  })),
  active: scenario.active,
});

const toPayload = (values: ScenarioFormValues): OutboundScenarioPayload => {
  const data = values.data_to_collect ?? [];
  return {
    name: values.name.trim(),
    objective: values.objective.trim(),
    success_criteria: values.success_criteria.trim(),
    opening_line: values.opening_line?.trim() || null,
    guardrails: (values.guardrails ?? []).map((rule) => rule.trim()).filter(Boolean),
    data_to_collect: data.length
      ? Object.fromEntries(data.map(({ key, description }) => [key.trim(), description.trim()]))
      : null,
    active: values.active,
  };
};

/** Form.Item hands its control `value`/`onChange`; Toggle speaks `checked`. */
function ToggleField({
  value,
  onChange,
  label,
}: {
  value?: boolean;
  onChange?: (checked: boolean) => void;
  label: string;
}) {
  return <Toggle checked={!!value} onChange={onChange} label={label} />;
}

interface ScenarioFormModalProps {
  open: boolean;
  companyId: number;
  /** Edits this scenario; creates a new one when absent. */
  scenario?: OutboundScenario;
  onClose: () => void;
}

export function ScenarioFormModal({ open, companyId, scenario, onClose }: ScenarioFormModalProps) {
  const { t } = useTranslation(['outbound', 'common']);
  const [form] = Form.useForm<ScenarioFormValues>();
  const { createScenario, isPending: isCreating } = useCreateOutboundScenario(companyId);
  const { updateScenario, isPending: isUpdating } = useUpdateOutboundScenario();

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (scenario) form.setFieldsValue(toFormValues(scenario));
  }, [open, scenario, form]);

  const handleSubmit = async (values: ScenarioFormValues) => {
    const payload = toPayload(values);
    try {
      if (scenario) await updateScenario({ id: scenario.id, payload });
      else await createScenario(payload);
      openNotification({ type: 'success', message: t('outbound:saved') });
      onClose();
    } catch (error) {
      const fields = formFieldErrors(error, FIELDS, {
        taken: { name: t('outbound:scenarioForm.validation.nameTaken') },
      });
      if (fields.length) {
        form.setFields(fields);
        return;
      }
      openNotification({ type: 'error', message: t('outbound:scenarioForm.saveFailed') });
    }
  };

  const required = (message: string) => [{ required: true, whitespace: true, message }];

  return (
    <FormModal
      open={open}
      title={
        scenario ? t('outbound:scenarioForm.editTitle') : t('outbound:scenarioForm.createTitle')
      }
      subtitle={t('outbound:scenarioForm.subtitle')}
      submitLabel={scenario ? t('common:actions.save') : t('common:actions.create')}
      onSubmit={() => form.submit()}
      onCancel={onClose}
      isLoading={isCreating || isUpdating}
      width={680}
    >
      <Form<ScenarioFormValues>
        form={form}
        name="scenario"
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ guardrails: [], data_to_collect: [], active: true, opening_line: '' }}
      >
        <Form.Item
          label={t('outbound:scenarioForm.fields.name')}
          name="name"
          rules={required(t('outbound:scenarioForm.validation.nameRequired'))}
        >
          <Input maxLength={255} />
        </Form.Item>

        <Form.Item
          label={t('outbound:scenarioForm.fields.objective')}
          name="objective"
          extra={t('outbound:scenarioForm.help.objective')}
          rules={required(t('outbound:scenarioForm.validation.objectiveRequired'))}
        >
          <Input.TextArea autoSize={{ minRows: 3, maxRows: 8 }} />
        </Form.Item>

        <Form.Item
          label={t('outbound:scenarioForm.fields.successCriteria')}
          name="success_criteria"
          rules={required(t('outbound:scenarioForm.validation.successCriteriaRequired'))}
        >
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} />
        </Form.Item>

        <Form.Item
          label={t('outbound:scenarioForm.fields.openingLine')}
          name="opening_line"
          extra={t('outbound:scenarioForm.help.openingLine')}
        >
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
        </Form.Item>

        <Form.Item
          label={t('outbound:scenarioForm.fields.guardrails')}
          extra={t('outbound:scenarioForm.help.guardrails')}
        >
          <Form.List name="guardrails">
            {(fields, { add, remove }) => (
              <div className={styles.rows}>
                {fields.map(({ key, ...field }, index) => (
                  <div key={key} className={styles.fieldRow}>
                    <Form.Item
                      {...field}
                      className={styles.grow}
                      rules={required(t('outbound:scenarioForm.validation.guardrailRequired'))}
                    >
                      <Input.TextArea
                        autoSize={{ minRows: 1, maxRows: 4 }}
                        aria-label={`${t('outbound:scenarioForm.fields.guardrails')} ${index + 1}`}
                      />
                    </Form.Item>
                    <IconButton
                      label={t('outbound:scenarioForm.removeRow', { index: index + 1 })}
                      onClick={() => remove(field.name)}
                    >
                      <TrashIcon />
                    </IconButton>
                  </div>
                ))}
                <div>
                  <Button buttonVariant="secondary" onClick={() => add('')}>
                    {t('outbound:scenarioForm.addGuardrail')}
                  </Button>
                </div>
              </div>
            )}
          </Form.List>
        </Form.Item>

        <Form.Item
          label={t('outbound:scenarioForm.fields.dataToCollect')}
          extra={t('outbound:scenarioForm.help.dataToCollect', { max: MAX_DATA_TO_COLLECT_KEYS })}
        >
          <Form.List name="data_to_collect">
            {(fields, { add, remove }) => (
              <div className={styles.rows}>
                {fields.map(({ key, ...field }, index) => (
                  <div key={key} className={styles.fieldRow}>
                    <Form.Item
                      className={styles.dataKey}
                      name={[field.name, 'key']}
                      rules={[
                        ...required(t('outbound:scenarioForm.validation.dataKeyRequired')),
                        {
                          pattern: DATA_KEY_PATTERN,
                          message: t('outbound:scenarioForm.validation.dataKeyFormat'),
                        },
                        ({ getFieldValue }) => ({
                          validator: (_, value?: string) => {
                            const others = (getFieldValue('data_to_collect') as DataField[])
                              .filter((_item, position) => position !== index)
                              .map((item) => item?.key);
                            return value && others.includes(value)
                              ? Promise.reject(
                                  new Error(t('outbound:scenarioForm.validation.dataKeyDuplicate'))
                                )
                              : Promise.resolve();
                          },
                        }),
                      ]}
                    >
                      <Input
                        aria-label={`${t('outbound:scenarioForm.fields.dataKey')} ${index + 1}`}
                        placeholder={t('outbound:scenarioForm.fields.dataKey')}
                      />
                    </Form.Item>
                    <Form.Item className={styles.grow} name={[field.name, 'description']}>
                      <Input
                        aria-label={`${t('outbound:scenarioForm.fields.dataDescription')} ${index + 1}`}
                        placeholder={t('outbound:scenarioForm.fields.dataDescription')}
                      />
                    </Form.Item>
                    <IconButton
                      label={t('outbound:scenarioForm.removeRow', { index: index + 1 })}
                      onClick={() => remove(field.name)}
                    >
                      <TrashIcon />
                    </IconButton>
                  </div>
                ))}
                {fields.length < MAX_DATA_TO_COLLECT_KEYS && (
                  <div>
                    <Button
                      buttonVariant="secondary"
                      onClick={() => add({ key: '', description: '' })}
                    >
                      {t('outbound:scenarioForm.addDataKey')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Form.List>
        </Form.Item>

        <Form.Item label={t('outbound:scenarioForm.fields.active')} name="active">
          <ToggleField label={t('outbound:scenarioForm.fields.active')} />
        </Form.Item>
      </Form>
    </FormModal>
  );
}
